import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddFAQsParams, AIModelType, createRobotDatasets, useNewBotFAQsMethods } from '@mygpt/app';
import { generationFAQsByFileSSE } from '@mygpt/app/faqs';

import { useCreateStore } from '@/store/create';
import { useGPTStore } from '@/store';
import { useLocation } from 'react-router-dom';

interface GenerFAQ {
  id: string;
  file: File;
}

interface BatchAddFAQ {
  id: string;
  faqs: FaqsItem[];
}

type FaqsItem = Omit<AddFAQsParams, 'aiId'>;

export const useTaskFAQ = () => {
  const { t } = useTranslation();

  const { NewBotBatchAddFAQs } = useNewBotFAQsMethods();
  const { datasetId, embeddingModelName, pageType, setPageType, setBotId, setDatasetId } = useCreateStore();
  const { assetScope } = useGPTStore();
  const isCloudMode = import.meta.env.VITE_MODE !== 'local';

  const [taskProcess, setTaskProcess] = useState<number>(0);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  // 创建bot createRobots
  // 上传文件 generationFAQsByFileSSE(id, file) 机器人工作进度 handleProgress
  // 导入问答库 batchAddFAQs

  const task_1 = async (file: File): Promise<GenerFAQ | undefined> => {
    let rspId = datasetId;

    if (pageType === 'create') {
      const fileName = file.name;
      const res = await createRobotDatasets({
        aiModel: AIModelType.FILE,
        name: fileName,
        embeddingModelName,
        ...(isCloudMode && assetScope && { companyId: assetScope }),
      });
      if (!res) return;
      const datasets = res.data;
      if (datasets.error) return Promise.reject(datasets.error);
      const botId = queryParams.get('botId');
      setBotId(botId || '');
      const { id: datasetId } = datasets;
      setDatasetId(datasetId);
      setPageType('modify');
      rspId = datasetId;
    }

    return { id: rspId, file };
  };
  const task_2 = async ({ file, id }: GenerFAQ): Promise<BatchAddFAQ | undefined> => {
    return new Promise((resolve, reject) => {
      const faqs: FaqsItem[] = [];

      // 判断文件类型是否为 Excel
      const isExcelFile =
        file.type === 'application/vnd.ms-excel' ||
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.name.endsWith('.xls') ||
        file.name.endsWith('.xlsx');

      // 使用统一的 SSE 服务函数
      const sse = generationFAQsByFileSSE({
        datasetId: id,
        file,
        // Excel 文件使用 template 模式，不传 count
        ...(isExcelFile ? { generation_type: 'template' } : {}),
      });

      // 监听消息事件
      sse.addEventListener('message', (e: any) => {
        try {
          if (!e.data || !e.data.trim()) return;

          const data = JSON.parse(e.data);
          const { action, progress, value } = data;
          console.log('SSE FAQs generation progress:', value, data);

          for (const item of value) {
            faqs.push({
              datasetId: id,
              ...item,
              sources: item.sources || (typeof file === 'object' ? file.name : file) || 'system',
            });
          }

          setTaskProcess(progress);

          if (action === 'done') {
            if (faqs.length) {
              resolve({ id, faqs });
            } else {
              reject(t('errors.faq_extract_error'));
            }
            sse.close();
          }
        } catch (error) {
          console.error(`Failed to parse chunkValue(${e.data}) error`, error);
        }
      });

      // 监听错误事件
      sse.addEventListener('error', (e: any) => {
        console.error('SSE error:', e);
        reject(t('errors.faq_extract_error'));
        sse.close();
      });

      // 监听连接关闭事件
      sse.addEventListener('abort', () => {
        console.log('SSE connection aborted');
        sse.close();
      });

      // 监听连接就绪事件（readyState === 2 表示连接关闭）
      sse.addEventListener('readystatechange', (e: any) => {
        console.log('SSE readyState changed:', e.readyState);
        if (e.readyState === 2) {
          // 连接已关闭
          sse.close();
          setTaskProcess(100);
          resolve({ id, faqs });
        }
      });
    });
  };
  const task_3 = async ({ faqs, id }: BatchAddFAQ): Promise<boolean | undefined> => {
    return new Promise((resolve, reject) => {
      const randomDelay = Math.floor(Math.random() * 5000) + 5000; // 5000-10000ms
      let isResolved = false;

      // 设置最小延迟时间
      const minDelayTimer = setTimeout(() => {
        if (!isResolved) {
          isResolved = true;
          resolve(true);
        }
      }, randomDelay);

      // 调用接口
      NewBotBatchAddFAQs({
        datasetId: id,
        data: faqs.map(({ datasetId: id, ...item }) => item),
      })
        .then(() => {
          // 接口成功返回后,如果还没resolve,立即resolve
          if (!isResolved) {
            clearTimeout(minDelayTimer);
            isResolved = true;
            resolve(true);
          }
        })
        .catch((err: unknown) => {
          // 接口失败,清除定时器并reject
          clearTimeout(minDelayTimer);
          if (!isResolved) {
            isResolved = true;
            reject(err);
          }
        });
    });
  };

  return {
    taskProcess,
    tasks: [task_1, task_2, task_3],
  };
};
